<template>
  <div class="merchant-products-page">
    <div class="header">
      <div>
        <h1>商家商品管理</h1>
        <p class="sub">上架/下架、库存调整、价格调整（真实版）</p>
      </div>
      <div class="header-actions">
        <el-button @click="goDashboard">返回工作台</el-button>
        <el-button type="primary" plain @click="reloadFromServer" :loading="loading">重载商品</el-button>
        <el-dropdown @command="handleUserCommand">
          <span class="user-dropdown-link">
            {{ merchantName }}
            <el-icon class="el-icon--right"><ArrowDown /></el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="profile">个人信息</el-dropdown-item>
              <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>

    <div class="toolbar glass">
      <el-input v-model="keyword" placeholder="搜索商品名" clearable style="width: 220px;" />
      <el-select v-model="categoryFilter" placeholder="全部分类" clearable style="width: 150px;">
        <el-option v-for="c in categories" :key="c" :label="c" :value="c" />
      </el-select>
      <el-select v-model="statusFilter" style="width: 130px;">
        <el-option label="全部状态" value="all" />
        <el-option label="已上架" value="on" />
        <el-option label="已下架" value="off" />
      </el-select>
      <el-button type="success" @click="openCreateDialog">上架商品</el-button>
      <el-button type="primary" @click="batchSave" :disabled="!hasDirtyRows">保存修改</el-button>
    </div>

    <div class="table-wrap glass">
      <el-table :data="filteredList" stripe v-loading="loading" height="560" empty-text="暂无商品数据">
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="title" label="商品" min-width="180" show-overflow-tooltip />
        <el-table-column prop="category" label="分类" width="110" />

        <el-table-column label="价格" width="140">
          <template #default="scope">
            <el-input-number
              v-model="scope.row.price"
              :min="0"
              :precision="2"
              :step="1"
              size="small"
              @change="markDirty(scope.row)"
            />
          </template>
        </el-table-column>

        <el-table-column label="库存" width="130">
          <template #default="scope">
            <el-input-number
              v-model="scope.row.stock"
              :min="0"
              :step="1"
              size="small"
              @change="markDirty(scope.row)"
            />
          </template>
        </el-table-column>

        <el-table-column label="状态" width="130">
          <template #default="scope">
            <el-switch
              v-model="scope.row.enabled"
              active-text="上架"
              inactive-text="下架"
              @change="changeStatus(scope.row)"
            />
          </template>
        </el-table-column>

        <el-table-column label="修改标记" width="110">
          <template #default="scope">
            <el-tag v-if="scope.row.dirty" type="warning" size="small">待保存</el-tag>
            <el-tag v-else type="info" size="small">已同步</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="90" fixed="right">
          <template #default="scope">
            <el-button link type="danger" @click="removeProduct(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div class="tips">
      <el-alert title="说明：当前页面已接入 product-service 真 CRUD，修改将写入数据库。" type="success" :closable="false" />
    </div>

    <el-dialog
      v-model="createDialogVisible"
      title="上架新商品"
      width="460px"
      :modal="false"
      :lock-scroll="false"
    >
      <el-form :model="createForm" label-width="76px">
        <el-form-item label="商品名"><el-input v-model="createForm.title" /></el-form-item>
        <el-form-item label="分类"><el-input v-model="createForm.category" /></el-form-item>
        <el-form-item label="价格"><el-input-number v-model="createForm.price" :min="0" :precision="2" style="width: 100%;" /></el-form-item>
        <el-form-item label="库存"><el-input-number v-model="createForm.stock" :min="0" style="width: 100%;" /></el-form-item>
        <el-form-item label="图片"><el-input v-model="createForm.picUrl" placeholder="可选" /></el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="creating" @click="createProduct">立即上架</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowDown } from '@element-plus/icons-vue'
import { productService } from '../../api/product'

const router = useRouter()
const route = useRoute()
const loading = ref(false)
const productList = ref([])
const keyword = ref('')
const categoryFilter = ref('')
const statusFilter = ref('all')
const merchantName = ref('演示商家')
const createDialogVisible = ref(false)
const creating = ref(false)
const createForm = ref({
  title: '',
  category: '',
  price: 0,
  stock: 0,
  picUrl: ''
})

const hasDirtyRows = computed(() => productList.value.some(item => item.dirty))

const categories = computed(() => Array.from(new Set(productList.value.map(i => i.category).filter(Boolean))))

const filteredList = computed(() => {
  const kw = keyword.value.trim().toLowerCase()
  return productList.value.filter(item => {
    const byKeyword = !kw || (item.title || '').toLowerCase().includes(kw)
    const byCategory = !categoryFilter.value || item.category === categoryFilter.value
    const byStatus = statusFilter.value === 'all' || (statusFilter.value === 'on' ? item.enabled : !item.enabled)
    return byKeyword && byCategory && byStatus
  })
})

const parseProducts = (payload) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.records)) return payload.records
  if (Array.isArray(payload?.list)) return payload.list
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.records)) return payload.data.records
  if (Array.isArray(payload?.data?.list)) return payload.data.list
  return []
}

const normalizeProduct = (item) => ({
  ...item,
  price: Number(item.price ?? 0),
  stock: Number(item.stock ?? 0),
  enabled: Number(item.status ?? 1) === 1,
  dirty: false,
})

const loadProducts = async () => {
  loading.value = true
  try {
    const response = await productService.getProductList({ page: 1, size: 300 })
    const list = parseProducts(response.data)
    productList.value = list.map(normalizeProduct)
  } catch (error) {
    ElMessage.error(error.response?.data?.message || error.message || '加载商品失败')
  } finally {
    loading.value = false
  }
}

const batchSave = async () => {
  const dirtyRows = productList.value.filter(item => item.dirty)
  if (!dirtyRows.length) {
    ElMessage.info('没有需要保存的修改')
    return
  }

  loading.value = true
  try {
    for (const item of dirtyRows) {
      await productService.updateProduct(item.id, {
        title: item.title,
        category: item.category,
        price: item.price,
        stock: item.stock,
        picUrl: item.picUrl,
        status: item.enabled ? 1 : 0,
      })
      item.dirty = false
    }
    ElMessage.success(`已保存 ${dirtyRows.length} 条商品修改`)
    await loadProducts()
  } catch (error) {
    ElMessage.error(error.response?.data?.message || error.message || '保存失败')
  } finally {
    loading.value = false
  }
}

const markDirty = (row) => {
  row.dirty = true
}

const changeStatus = async (row) => {
  try {
    await productService.updateProductStatus(row.id, row.enabled ? 1 : 0)
    ElMessage.success(`${row.title} 已${row.enabled ? '上架' : '下架'}`)
    row.dirty = false
    row.status = row.enabled ? 1 : 0
  } catch (error) {
    row.enabled = !row.enabled
    ElMessage.error(error.response?.data?.message || error.message || '更新状态失败')
  }
}

const removeProduct = async (row) => {
  try {
    await ElMessageBox.confirm(`确定删除商品「${row.title}」吗？`, '删除确认', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning',
    })
    await productService.deleteProduct(row.id)
    productList.value = productList.value.filter(item => item.id !== row.id)
    ElMessage.success('删除成功')
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.response?.data?.message || error.message || '删除失败')
    }
  }
}

const openCreateDialog = () => {
  createForm.value = {
    title: '',
    category: '',
    price: 0,
    stock: 0,
    picUrl: '',
  }
  createDialogVisible.value = true
}

const tryOpenCreateFromQuery = () => {
  if (route.query.action === 'create') {
    openCreateDialog()
    router.replace('/merchant/products')
  }
}

const createProduct = async () => {
  if (!createForm.value.title.trim()) {
    ElMessage.warning('请输入商品名')
    return
  }
  creating.value = true
  try {
    await productService.createProduct({
      title: createForm.value.title.trim(),
      category: createForm.value.category?.trim() || '未分类',
      price: Number(createForm.value.price || 0),
      stock: Number(createForm.value.stock || 0),
      picUrl: createForm.value.picUrl?.trim() || null,
      status: 1,
    })
    createDialogVisible.value = false
    ElMessage.success('商品创建成功')
    await loadProducts()
  } catch (error) {
    ElMessage.error(error.response?.data?.message || error.message || '创建失败')
  } finally {
    creating.value = false
  }
}

const goDashboard = () => router.push('/merchant')

const logout = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  localStorage.removeItem('portalRole')
  router.push('/login')
}

const goProfile = () => {
  router.push('/profile')
}

const handleUserCommand = (command) => {
  if (command === 'profile') return goProfile()
  if (command === 'logout') return logout()
}

const reloadFromServer = async () => {
  await loadProducts()
  ElMessage.success('已从后端重载商品')
}

onMounted(() => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    merchantName.value = user.nickname || user.username || '演示商家'
  } catch {
    merchantName.value = '演示商家'
  }
  loadProducts()
  tryOpenCreateFromQuery()
})
</script>

<style scoped>
.merchant-products-page {
  min-height: 100vh;
  padding: 22px;
  color: #fff;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.header h1 {
  margin: 0;
  font-size: 1.6rem;
}

.sub {
  margin: 6px 0 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.9rem;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-dropdown-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: #fff;
  cursor: pointer;
}

.toolbar {
  margin-top: 10px;
  margin-bottom: 12px;
  padding: 12px;
  display: flex;
  gap: 10px;
  align-items: center;
}

.table-wrap {
  padding: 10px;
  border-radius: 12px;
}

.glass {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
}

.tips {
  margin-top: 12px;
}

:deep(.el-table) {
  --el-table-tr-bg-color: rgba(255, 255, 255, 0.96);
  --el-table-header-bg-color: rgba(244, 246, 252, 0.98);
  --el-table-row-hover-bg-color: rgba(83, 124, 229, 0.08);
  border-radius: 10px;
}

:deep(.el-table th.el-table__cell) {
  color: #333;
}

:deep(.el-table td.el-table__cell) {
  color: #2d3550;
}
</style>
